import { useState } from "react";
import type { FormEvent } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";

import { errorMessage } from "@/api/errors";
import type { Role } from "@/api/types";
import { useAuth } from "@/auth/AuthProvider";
import { homePath } from "@/auth/roles";
import { EXAMPLE } from "@/lib/examples";
import { email as checkEmail, focusField } from "@/lib/fields";
import { Banner, Button, Logo, PasswordField, TextField } from "@/ui";

import s from "./LoginPage.module.css";

/**
 * Where to go after signing in: the in-app path the person was sent here from (a `/` followed
 * by anything but another `/`, so never a protocol or a `//host`), else their home.
 */
export function afterSignIn(from: unknown, role: Role): string {
  return typeof from === "string" && /^\/(?!\/)/.test(from) ? from : homePath(role);
}

export function LoginPage() {
  const { status, user, signIn } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [problems, setProblems] = useState<{ email?: string; password?: string }>({});

  if (status === "signed-in" && user) return <Navigate to={afterSignIn((location.state as { from?: unknown } | null)?.from, user.role)} replace />;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    // Signing in only needs an email address in the right form and a password; the
    // password rules apply when a password is chosen, not here.
    const address = checkEmail(email);
    const found = { email: address.ok ? undefined : address.message, password: password ? undefined : "Enter your password." };
    setProblems(found);
    if (!address.ok || !password) {
      setError(null);
      focusField(found.email ? "email" : "password");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await signIn(address.value, password);
    } catch (cause) {
      setError(errorMessage(cause));
      setBusy(false);
    }
  }

  return (
    <main className={s.screen}>
      <form className={s.card} onSubmit={onSubmit} noValidate>
        <Link to="/" aria-label="Zotula home"><Logo height={32} /></Link>
        <div>
          <h1 className={s.title}>Sign in</h1>
          <p className={s.subtitle}>Use your Zotula work account.</p>
        </div>
        {error ? <Banner>{error}</Banner> : null}
        <TextField label="Email" type="email" placeholder={EXAMPLE.email} name="email" autoComplete="username" autoFocus data-field="email" value={email} onChange={(e) => setEmail(e.target.value)} error={problems.email} />
        <PasswordField label="Password" placeholder={EXAMPLE.currentPassword} name="password" autoComplete="current-password" data-field="password" value={password} onChange={(e) => setPassword(e.target.value)} error={problems.password} />
        <Button type="submit" variant="primary" block busy={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </Button>
        <p className={s.forgot}><Link to="/forgot-password" state={{ email: email.trim() }}>Forgot your password?</Link></p>
        <p className={s.switch}>New to Zotula? <Link to="/signup">Create your company's account</Link></p>
      </form>
    </main>
  );
}
